import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type {
  OrderIssue,
  OrderIssueStats,
  OrderIssueFilters,
  UpsertOrderIssueParams,
} from '../types/orderIssues';

const DEFAULT_FILTERS: OrderIssueFilters = {
  search: '',
  issueType: 'all',
  status: 'open',
};

export function useOrderIssues() {
  const { user } = useAuth();
  const adminId = getAdminId(user);

  const [issues, setIssues] = useState<OrderIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState<OrderIssueFilters>(DEFAULT_FILTERS);
  const [stats, setStats] = useState<OrderIssueStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadIssues = useCallback(
    async (overrideFilters?: Partial<OrderIssueFilters>, overridePage?: number) => {
      if (!adminId) return;
      setLoading(true);
      setError(null);

      const f = { ...filters, ...overrideFilters };
      const p = overridePage ?? page;

      try {
        const { data, error: rpcError } = await supabase.rpc('get_open_order_issues', {
          p_admin_id: adminId,
          p_search: f.search || null,
          p_issue_type_filter: f.issueType === 'all' ? null : f.issueType,
          p_status_filter: f.status === 'all' ? 'all' : f.status,
          p_limit: pageSize,
          p_offset: p * pageSize,
        });

        if (rpcError) throw rpcError;

        const rows = (data || []) as OrderIssue[];
        setIssues(rows);
        setTotal(rows.length > 0 ? Number(rows[0].total_count) || 0 : 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load issues');
      } finally {
        setLoading(false);
      }
    },
    [adminId, filters, page, pageSize]
  );

  const loadStats = useCallback(async () => {
    if (!adminId) return;
    setStatsLoading(true);

    try {
      const { data, error: rpcError } = await supabase.rpc('get_order_issues_stats', {
        p_admin_id: adminId,
      });

      if (rpcError) throw rpcError;

      const row = Array.isArray(data) ? data[0] : data;
      if (row) {
        setStats({
          total_open: Number(row.total_open) || 0,
          total_resolved: Number(row.total_resolved) || 0,
          total_ignored: Number(row.total_ignored) || 0,
          total_under_process: Number(row.total_under_process) || 0,
          total_processed: Number(row.total_processed) || 0,
          label_generation_failed_count: Number(row.label_generation_failed_count) || 0,
          address_validation_error_count: Number(row.address_validation_error_count) || 0,
          postnl_api_error_count: Number(row.postnl_api_error_count) || 0,
          shopify_fulfillment_error_count: Number(row.shopify_fulfillment_error_count) || 0,
          incomplete_address_count: Number(row.incomplete_address_count) || 0,
          postal_code_invalid_count: Number(row.postal_code_invalid_count) || 0,
          other_count: Number(row.other_count) || 0,
        });
      }
    } catch {
      // stats are non-critical
    } finally {
      setStatsLoading(false);
    }
  }, [adminId]);

  const upsertIssue = useCallback(
    async (params: UpsertOrderIssueParams) => {
      try {
        await supabase.rpc('upsert_order_issue', {
          p_admin_id: params.adminId,
          p_order_reference: params.orderReference,
          p_shopify_order_id: params.shopifyOrderId ?? null,
          p_client_name: params.clientName ?? null,
          p_recipient_address: params.recipientAddress ?? {},
          p_issue_type: params.issueType,
          p_issue_description: params.issueDescription,
          p_error_code: params.errorCode ?? null,
          p_source: params.source,
        });
      } catch (err) {
        console.error('Failed to upsert order issue:', err);
      }
    },
    []
  );

  const resolveIssueByReference = useCallback(
    async (orderReference: string) => {
      if (!adminId || !orderReference) return;
      try {
        await supabase.rpc('resolve_order_issue_by_reference', {
          p_admin_id: adminId,
          p_order_reference: orderReference,
        });
      } catch (err) {
        console.error('Failed to resolve order issue:', err);
      }
    },
    [adminId]
  );

  const resolveIssue = useCallback(
    async (issueId: string, note?: string) => {
      if (!adminId) return;
      try {
        await supabase.rpc('bulk_resolve_order_issues', {
          p_admin_id: adminId,
          p_issue_ids: [issueId],
          p_resolved_by: user?.id ?? adminId,
          p_resolution_note: note ?? null,
        });
        await loadIssues();
        await loadStats();
      } catch (err) {
        console.error('Failed to resolve issue:', err);
      }
    },
    [adminId, user?.id, loadIssues, loadStats]
  );

  const ignoreIssue = useCallback(
    async (issueId: string) => {
      if (!adminId) return;
      try {
        await supabase.rpc('bulk_ignore_order_issues', {
          p_admin_id: adminId,
          p_issue_ids: [issueId],
        });
        await loadIssues();
        await loadStats();
      } catch (err) {
        console.error('Failed to ignore issue:', err);
      }
    },
    [adminId, loadIssues, loadStats]
  );

  const bulkResolve = useCallback(
    async (issueIds: string[], note?: string) => {
      if (!adminId || issueIds.length === 0) return;
      try {
        await supabase.rpc('bulk_resolve_order_issues', {
          p_admin_id: adminId,
          p_issue_ids: issueIds,
          p_resolved_by: user?.id ?? adminId,
          p_resolution_note: note ?? null,
        });
        await loadIssues();
        await loadStats();
      } catch (err) {
        console.error('Failed to bulk resolve:', err);
      }
    },
    [adminId, user?.id, loadIssues, loadStats]
  );

  const bulkIgnore = useCallback(
    async (issueIds: string[]) => {
      if (!adminId || issueIds.length === 0) return;
      try {
        await supabase.rpc('bulk_ignore_order_issues', {
          p_admin_id: adminId,
          p_issue_ids: issueIds,
        });
        await loadIssues();
        await loadStats();
      } catch (err) {
        console.error('Failed to bulk ignore:', err);
      }
    },
    [adminId, loadIssues, loadStats]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<OrderIssueFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
      setPage(0);
    },
    []
  );

  useEffect(() => {
    loadIssues();
    loadStats();
  }, [loadIssues, loadStats]);

  return {
    issues,
    loading,
    error,
    total,
    page,
    pageSize,
    filters,
    stats,
    statsLoading,
    setPage,
    updateFilters,
    loadIssues,
    loadStats,
    upsertIssue,
    resolveIssueByReference,
    resolveIssue,
    ignoreIssue,
    bulkResolve,
    bulkIgnore,
    adminId,
  };
}
